import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ImagePlus, X, Mic, Check, Dumbbell } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function fmtTs(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * ChatConversation — WhatsApp-style unified message stream shared by the
 * coach and athlete portals. Renders DMs and workout comments in one
 * scroll; workout comments get a small context chip linking back to the
 * workout (contextLink builds the URL per side). Supports photo
 * attachments and a live typing indicator.
 *
 * Props:
 *   messages: merged stream items (kind: "dm" | "workout")
 *   side: "coach" | "athlete" — which role is "me" (right-aligned)
 *   onSend: async (text, photoBase64[] , audioBase64|null) => saved message doc
 *   contextLink: (m) => url string for workout-comment chips (optional)
 *   otherTyping: bool — the other side is typing right now
 *   otherLabel: display name for the typing indicator ("Coach" / athlete name)
 *   onTyping: () => void — heartbeat fired (throttled) while the user types
 */
export default function ChatConversation({
  messages,
  side,
  onSend,
  contextLink,
  otherTyping = false,
  otherLabel = "",
  onTyping,
}) {
  const [items, setItems] = useState(messages || []);
  const [draft, setDraft] = useState("");
  const [photos, setPhotos] = useState([]); // data URLs (max 6)
  const [audio, setAudio] = useState(null); // data URL (voice note)
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastTypingPingRef = useRef(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const discardRef = useRef(false);

  useEffect(() => () => stopTracks(), []);

  const stopTracks = () => {
    try {
      recorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4", "audio/ogg"].find(
        (m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)
      );
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      discardRef.current = false;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stopTracks();
        if (discardRef.current) return;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 8 * 1024 * 1024) {
          toast.error("Voice note too long — try a shorter one");
          return;
        }
        const reader = new FileReader();
        reader.onload = () => setAudio(reader.result);
        reader.readAsDataURL(blob);
      };
      rec.start();
      setRecSecs(0);
      setRecording(true);
      recTimerRef.current = setInterval(() => {
        setRecSecs((s) => {
          if (s + 1 >= 120) finishRecording(); // auto-stop at 2 min
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Microphone unavailable — check browser permissions");
    }
  };

  const finishRecording = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    try {
      recorderRef.current?.stop();
    } catch { /* noop */ }
  };

  const cancelRecording = () => {
    discardRef.current = true;
    clearInterval(recTimerRef.current);
    setRecording(false);
    try {
      recorderRef.current?.stop();
    } catch { /* noop */ }
  };

  useEffect(() => {
    setItems(messages || []);
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items.length, otherTyping]);

  const pingTyping = () => {
    if (!onTyping) return;
    const now = Date.now();
    if (now - lastTypingPingRef.current > 2500) {
      lastTypingPingRef.current = now;
      onTyping();
    }
  };

  const MAX_PHOTOS = 6;
  const pickPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Max ${MAX_PHOTOS} photos per message`);
      return;
    }
    const accepted = files.slice(0, room);
    if (files.length > room) toast.error(`Only ${room} more photo${room === 1 ? "" : "s"} allowed (max ${MAX_PHOTOS})`);
    accepted.forEach((file) => {
      if (file.size > 4 * 1024 * 1024) {
        toast.error(`${file.name} is too large — max 4 MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((prev) => (prev.length < MAX_PHOTOS ? [...prev, reader.result] : prev));
      reader.readAsDataURL(file);
    });
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if ((!text && photos.length === 0 && !audio) || sending) return;
    setSending(true);
    try {
      const saved = await onSend(text, photos, audio);
      setItems((prev) => [...prev, { ...saved, kind: "dm" }]);
      setDraft("");
      setPhotos([]);
      setAudio(null);
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="chat-conversation">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
        {items.length === 0 && !otherTyping ? (
          <div
            className="text-center text-sm text-[var(--tir-muted)] italic py-10"
            data-testid="chat-empty"
          >
            {side === "athlete"
              ? "No messages yet — say hi to your coach! Notes you leave on workouts show up here too."
              : "No messages yet — send the first one. Workout notes and replies show up here too."}
          </div>
        ) : (
          items.map((m) => {
            const isMe = m.from_role === side;
            const isCoach = m.from_role === "coach";
            const chipUrl =
              m.kind === "workout" && contextLink ? contextLink(m) : null;
            return (
              <div
                key={m.id}
                className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
                data-testid={`chat-msg-${m.id}`}
              >
                {m.kind === "workout" && (() => {
                  const when = m.workout_date
                    ? new Date(m.workout_date + "T00:00:00").toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })
                    : null;
                  const label = `Re: ${m.workout_title || "Workout"}${
                    when ? ` on ${when}` : ` · W${m.week_number} ${m.day_label}`
                  }`;
                  const chipCls =
                    "mb-1 inline-flex items-center gap-1.5 text-[12px] font-semibold normal-case px-3 py-1.5 rounded-md border";
                  return chipUrl ? (
                    <Link
                      to={chipUrl}
                      className={`${chipCls} text-[var(--tir-navy)] bg-[var(--tir-navy)]/10 border-[var(--tir-navy)]/25 hover:bg-[var(--tir-navy)]/20`}
                      data-testid={`chat-context-chip-${m.id}`}
                    >
                      <Dumbbell size={12} className="shrink-0" /> {label}
                    </Link>
                  ) : (
                    <span className={`${chipCls} text-[var(--tir-ink)]/70 bg-black/5 border-black/10`}>
                      <Dumbbell size={12} className="shrink-0" /> {label}
                    </span>
                  );
                })()}
                <div
                  className={`max-w-[85%] sm:max-w-[70%] rounded-2xl overflow-hidden ${
                    isMe
                      ? "bg-[var(--tir-burgundy)] text-white rounded-br-sm"
                      : "bg-white border border-[var(--tir-border)] text-[var(--tir-ink)] rounded-bl-sm"
                  }`}
                >
                  {(() => {
                    const urls = m.photo_urls?.length ? m.photo_urls : m.photo_url ? [m.photo_url] : [];
                    if (!urls.length) return null;
                    const multi = urls.length > 1;
                    return (
                      <div className={multi ? "grid grid-cols-2 gap-0.5" : ""} data-testid={`chat-photos-${m.id}`}>
                        {urls.map((u, i) => (
                          <a
                            key={`${u}-${i}`}
                            href={`${BACKEND}${u}`}
                            className={multi && urls.length % 2 === 1 && i === urls.length - 1 ? "col-span-2" : ""}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={i === 0 ? `chat-photo-${m.id}` : `chat-photo-${m.id}-${i}`}
                          >
                            <img
                              src={`${BACKEND}${u}`}
                              alt={`Attachment ${i + 1}`}
                              loading="lazy"
                              className={`${multi ? "block h-32 w-full object-cover" : "block max-h-64 w-auto max-w-full object-cover"} ${multi && urls.length % 2 === 1 && i === urls.length - 1 ? "col-span-2" : ""}`}
                            />
                          </a>
                        ))}
                      </div>
                    );
                  })()}
                  {m.audio_url && (
                    <audio
                      controls
                      preload="none"
                      src={`${BACKEND}${m.audio_url}`}
                      className="block w-56 max-w-full my-1.5 mx-1.5"
                      data-testid={`chat-audio-${m.id}`}
                    />
                  )}
                  {m.body && (
                    <div className="text-[13px] whitespace-pre-wrap leading-snug px-3.5 py-2">
                      {m.body}
                    </div>
                  )}
                </div>
                <div className="mt-0.5 text-[9px] font-mono-tir text-[var(--tir-muted)]">
                  {!isMe && (
                    <span className={isCoach ? "text-[var(--tir-burgundy)] font-semibold" : "text-[var(--tir-navy)] font-semibold"}>
                      {m.from_name} ·{" "}
                    </span>
                  )}
                  {fmtTs(m.created_at)}
                </div>
              </div>
            );
          })
        )}
        {otherTyping && (
          <div className="flex items-center gap-2" data-testid="chat-typing-indicator">
            <div className="bg-white border border-[var(--tir-border)] rounded-2xl rounded-bl-sm px-3.5 py-2.5 inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--tir-muted)] animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--tir-muted)] animate-bounce" style={{ animationDelay: "140ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--tir-muted)] animate-bounce" style={{ animationDelay: "280ms" }} />
            </div>
            <span className="text-[10px] italic text-[var(--tir-muted)]">
              {otherLabel || "…"} is typing
            </span>
          </div>
        )}
      </div>
      {photos.length > 0 && (
        <div className="border-t border-[var(--tir-border)] bg-white px-3 pt-2 flex items-start gap-2 flex-wrap" data-testid="chat-photo-preview">
          {photos.map((p, i) => (
            <div className="relative" key={i}>
              <img src={p} alt={`Preview ${i + 1}`} className="h-16 w-16 object-cover rounded-md border border-[var(--tir-border)]" />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                data-testid={i === 0 ? "chat-photo-remove" : `chat-photo-remove-${i}`}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--tir-ink)] text-white flex items-center justify-center"
                aria-label="Remove photo"
              >
                <X size={11} />
              </button>
            </div>
          ))}
          <span className="text-[10px] text-[var(--tir-muted)] italic mt-1" data-testid="chat-photo-count">
            {photos.length === 1 ? "Photo attached" : `${photos.length} photos attached`}
            {photos.length < MAX_PHOTOS ? " · tap the camera to add more" : ""}
          </span>
        </div>
      )}
      {audio && !recording && (
        <div className="border-t border-[var(--tir-border)] bg-white px-3 pt-2 flex items-center gap-2" data-testid="chat-audio-preview">
          <audio controls src={audio} className="h-9 flex-1 max-w-xs" />
          <button
            type="button"
            onClick={() => setAudio(null)}
            data-testid="chat-audio-remove"
            className="w-6 h-6 rounded-full bg-[var(--tir-ink)] text-white flex items-center justify-center shrink-0"
            aria-label="Discard voice note"
          >
            <X size={12} />
          </button>
          <span className="text-[10px] text-[var(--tir-muted)] italic">Voice note ready</span>
        </div>
      )}
      {recording ? (
        <div
          className="flex gap-3 items-center bg-white px-4 py-3 border-t border-[var(--tir-border)]"
          data-testid="chat-recording-bar"
        >
          <span className="w-3 h-3 rounded-full bg-red-600 animate-pulse shrink-0" />
          <span className="text-[13px] font-mono-tir text-[var(--tir-ink)]">
            {String(Math.floor(recSecs / 60))}:{String(recSecs % 60).padStart(2, "0")}
          </span>
          <span className="text-[11px] italic text-[var(--tir-muted)] flex-1">Recording…</span>
          <button
            type="button"
            onClick={cancelRecording}
            data-testid="chat-recording-cancel"
            className="w-9 h-9 rounded-full text-[var(--tir-muted)] hover:text-[var(--tir-ink)] hover:bg-[var(--tir-ivory)] flex items-center justify-center"
            aria-label="Cancel recording"
          >
            <X size={17} />
          </button>
          <button
            type="button"
            onClick={finishRecording}
            data-testid="chat-recording-done"
            className="w-9 h-9 rounded-full bg-[var(--tir-burgundy)] text-white flex items-center justify-center hover:bg-[var(--tir-burgundy-dark)]"
            aria-label="Finish recording"
          >
            <Check size={17} />
          </button>
        </div>
      ) : (
      <form
        onSubmit={send}
        className={`flex gap-2 items-end bg-white px-3 py-2.5 ${photos.length || audio ? "" : "border-t border-[var(--tir-border)]"}`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={pickPhoto}
          data-testid="chat-photo-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="chat-photo-btn"
          className="shrink-0 w-9 h-9 rounded-full text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-ivory)] flex items-center justify-center transition-colors"
          aria-label="Attach photos"
        >
          <ImagePlus size={17} />
        </button>
        <button
          type="button"
          onClick={startRecording}
          data-testid="chat-mic-btn"
          className="shrink-0 w-9 h-9 rounded-full text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-ivory)] flex items-center justify-center transition-colors"
          aria-label="Record voice note"
        >
          <Mic size={17} />
        </button>
        <textarea
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            pingTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Write a message…"
          data-testid="chat-input"
          className="flex-1 border border-[var(--tir-border)] bg-white rounded-full px-4 py-2 text-[13px] focus:outline-none focus:border-[var(--tir-burgundy)] resize-none leading-snug"
        />
        <button
          type="submit"
          disabled={(!draft.trim() && photos.length === 0 && !audio) || sending}
          data-testid="chat-send-btn"
          className="shrink-0 w-9 h-9 rounded-full bg-[var(--tir-burgundy)] text-white flex items-center justify-center hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Send message"
        >
          <Send size={15} />
        </button>
      </form>
      )}
    </div>
  );
}
